import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ILoggedInUser } from '../../interfaces/common/loggedInUser';
import { SearchParameters } from '../../shared/models/searchParameters.model';
import { PagedResult } from '../../shared/models/pagedResult.model';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { PersonSearchComponent } from '../../shared/person-search/person-search.component';
import { LoggedInUserService } from '../../services/common/logged-in-user.service';
import { ReportService } from '../../services/report/report.service';
import { IPerson } from '../../models/person.model';
import { SearchRequestService } from '../../manage-request/services/search-request.service';
import { ReportByModel } from '../../shared/constants/report-by.model';
import { CodeModel } from '../../administration/models/code.model';
import { CommonModule } from '@angular/common';
import { RequestAndLineTypeService } from '../../administration/services/request-and-line-types.service';
import { SearchSummaryReportModel } from '../models/search-report-summary.model';
import { SummaryReportModel } from '../models/summary-report.model';
import * as XLSX from 'xlsx';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorModule } from '@angular/material/paginator';

@Component({
  selector: 'app-summary-report',
  standalone: true,
  imports: [PersonSearchComponent, CommonModule, ReactiveFormsModule, MatProgressSpinnerModule, MatPaginatorModule, MatTableModule],
  templateUrl: './summary-report.component.html',
  styleUrl: './summary-report.component.scss'
})
export class SummaryReportComponent {
  searchForm: FormGroup;
  user: ILoggedInUser;
  isReportRight: boolean = false;
  isLoading: boolean = false;
  isLoadingExcel: boolean = false;
  totalCount: number;
  pageSize: number = 10;
  pageIndex: number;
  searchDone: boolean = false;
  displayedColumns: string[] = ['number', 'reportBy', 'nbrOfLocalRequests', 'localValue', 'nbrOfForeignRequests', 'foreignValue'];
  searchRequestParams: SearchParameters<SearchSummaryReportModel>;
  pagedData: PagedResult<SummaryReportModel[]>;
  dataSource: MatTableDataSource<SummaryReportModel>;
  divisionsList = [];
  requestTypesList = [];
  lineTypesList = [];
  reportByList: CodeModel [];
  reportBySelected: string;

  constructor(
    private userService: LoggedInUserService,
    private fb: FormBuilder,
    private reportService: ReportService,
    private searchRequestService: SearchRequestService,
    private requestAndLineTypeService : RequestAndLineTypeService ) {}

  ngOnInit(): void {
    this.reportByList = ReportByModel;

    this.searchForm = this.fb.group({
      requestFrom: new FormControl(''),          
      requestTo: new FormControl(''),
      requestDateFrom: new FormControl(''),
      requestDateTo: new FormControl(''),
      division: new FormControl(''),
      requester: new FormControl(''),
      approvedBy: new FormControl(''),
      reportBy: new FormControl(''),
      requestType: new FormControl(''),
      lineType: new FormControl('')
    });

    this.searchForm.get('reportBy').setValue('Division');
    this.reportBySelected = 'Division';

    this.userService.currentUser$.subscribe(data => {
      this.user = data;
      if(this.user !== null && this.user.reportRight){
        this.isReportRight = this.user.reportRight;

        this.getDivisions();
        this.getRequestTypes();        
      }      
    });
    
  }

  getFormValues():SearchParameters<SearchSummaryReportModel>{
    var searchParams = new SearchParameters<SearchSummaryReportModel>();
    searchParams.pageIndex = this.pageIndex;
    searchParams.pageSize = this.pageSize;
    searchParams.query = {
      requestFrom: this.searchForm.get('requestFrom').value || null,
      requestTo: this.searchForm.get('requestTo').value || null,
      requestDateFrom: this.searchForm.get('requestDateFrom').value || null,
      requestDateTo: this.searchForm.get('requestDateTo').value || null,
      division: this.searchForm.get('division').value || null,
      requester: this.searchForm.get('requester').value || null,
      approvedBy: this.searchForm.get('approvedBy').value || null,
      reportBy: this.searchForm.get('reportBy').value || null,
      requestType: this.searchForm.get('requestType').value || null,
      lineType: this.searchForm.get('lineType').value || null
    };
    return searchParams;  
  }

  submitForm() {

    if(this.isReportRight){
      this.isLoading = true;
      this.reportService.searchSummaryReport(this.getFormValues())
        .subscribe(data => {
          this.reportBySelected = this.searchForm.get('reportBy').value;
          this.pagedData = data;
          this.pageSize = this.pagedData.pageSize;
          this.totalCount = this.pagedData.totalCount;      
          this.dataSource = new MatTableDataSource<SummaryReportModel>(this.pagedData.items);
          this.isLoading = false;
          this.searchDone = true;
      });
    }
    else{
      this.dataSource = new MatTableDataSource<SummaryReportModel>([]);
    }
  }

  getDivisions(){
    if(this.isReportRight){
      this.searchRequestService.getAllDivisions().subscribe({
        next: (data) => {
          this.divisionsList = data;
        },
        error: (error) => {
        },
      });
    }    
  }

  getRequestTypes(){
    if(this.isReportRight){
      this.searchRequestService.getAllRequestTypes().subscribe({
        next: (data) => {
          this.requestTypesList = data;
        },
        error: (error) => {
        },
      });
    }    
  }s

  onRequestTypeSelect(event: any){
    if(event !== ''){
      this.getLineTypes(event);
    }
  }

  getLineTypes(requestType: string){
    if(this.isReportRight){
      this.requestAndLineTypeService.getLineTypeByRequest(requestType).subscribe({
        next: (data) => {
          this.lineTypesList = data;
        },
        error: (error) => {
        },
      });
    }    
  }

  onRequesterSelected(person: IPerson){
    this.searchForm.get('requester').patchValue(person.email.toString());
  }

  onRequesterClear(){
    this.searchForm.get('requester').setValue('');
  }

  onApproverSelected(person: IPerson){
    this.searchForm.get('approvedBy').setValue(person.email.toString());
  }

  onApproverClear(){
    this.searchForm.get('approvedBy').setValue('');
  }

  exportAllToExcel(){
    if(this.isReportRight){
      var searchParams = this.getFormValues();
      searchParams.pageSize = this.totalCount;
      this.isLoadingExcel = true;
      this.reportService.searchSummaryReport(searchParams).subscribe(data => {
        this.exportToExcel(data.items);
        this.isLoadingExcel = false;
      });
    }    
  }

  exportToExcel(data: SummaryReportModel[]) { 
    this.isLoadingExcel = true;
    if (this.isReportRight && data.length > 0) {
      const displayedColumns = this.displayedColumns.filter((col) => col !== 'number');
      var tempData = data;

      const filteredData = tempData.map((item, index) => {
        let newItem: { [key: string]: any } = {};
        displayedColumns.forEach((column) => { 
          if(column === 'reportBy'){
            switch (this.reportBySelected) {            
            case 'Division':
              newItem[column] = item.division
              break;
            case 'Charge AFE/CC':
              newItem[column] = item.mcu;
              break;
            case 'Request Type':
              newItem[column] = item.description;
              break;
            case 'Approver':
              newItem[column] = item.approver;
              break;
            case 'Month':
              newItem[column] = item.requestMonth;
              break;
            default:
              break;
            }            
          }else{
            newItem[column] = item[column];
          }         
          
        });
        return newItem;
        
      });
  
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(filteredData);  
      XLSX.utils.book_append_sheet(wb, ws, 'summary-raport');  
      XLSX.writeFile(wb, 'Summary-raport.xlsx');
      this.isLoadingExcel = false;
    }
  }
}
